/** Compliance routes (IA §2 Improve › Compliance). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';

const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => (allowed.includes(v as T) ? (v as T) : undefined);

export interface HoursSearch {
  q?: string;
  activity?: string;
  warnings?: 'yes';
  sort?: string;
  selected?: string;
}

export interface InfringementsSearch {
  driver?: string;
  vehicle?: string;
  rule?: string;
  severity?: 'MI' | 'SI' | 'VSI' | 'MSI';
  status?: 'open' | 'acknowledged' | 'withdrawn';
  sort?: string;
  selected?: string;
}

export interface FilesSearch {
  kind?: 'driver_card' | 'vehicle_unit';
  driver?: string;
  vehicle?: string;
  archived?: 'yes' | 'all';
  sort?: string;
  upload?: 'yes';
}

export interface DownloadsSearch {
  tab?: 'requests' | 'schedules' | 'attention';
  status?: 'scheduled' | 'requested' | 'in_progress' | 'completed' | 'failed' | 'timed_out';
  kind?: 'driver_card' | 'vehicle_unit';
  sort?: string;
  selected?: string;
}

export interface CardsSearch {
  selected?: string;
}

export const complianceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance',
  staticData: { permission: ['tacho.activity.read', 'tacho.infringement.read', 'tacho.file.read', 'tacho.download.read', 'tacho.card.read'] },
  component: lazyRouteComponent(() => import('./pages/OverviewPage')),
});

export const hoursRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/hours',
  staticData: { permission: 'tacho.activity.read' },
  validateSearch: (s: Record<string, unknown>): HoursSearch => ({
    q: str(s.q),
    activity: str(s.activity),
    warnings: s.warnings === 'yes' ? 'yes' : undefined,
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/HoursPage')),
});

export const infringementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/infringements',
  staticData: { permission: 'tacho.infringement.read' },
  validateSearch: (s: Record<string, unknown>): InfringementsSearch => ({
    driver: str(s.driver),
    vehicle: str(s.vehicle),
    rule: str(s.rule),
    severity: oneOf(s.severity, ['MI', 'SI', 'VSI', 'MSI'] as const),
    status: oneOf(s.status, ['open', 'acknowledged', 'withdrawn'] as const),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/InfringementsPage')),
});

export const filesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/files',
  staticData: { permission: 'tacho.file.read' },
  validateSearch: (s: Record<string, unknown>): FilesSearch => ({
    kind: oneOf(s.kind, ['driver_card', 'vehicle_unit'] as const),
    driver: str(s.driver),
    vehicle: str(s.vehicle),
    archived: oneOf(s.archived, ['yes', 'all'] as const),
    sort: str(s.sort),
    upload: s.upload === 'yes' ? 'yes' : undefined,
  }),
  component: lazyRouteComponent(() => import('./pages/FilesPage')),
});

export const fileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/files/$fileId',
  staticData: { permission: 'tacho.file.read' },
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({ tab: str(s.tab) }),
  component: lazyRouteComponent(() => import('./pages/FilePage')),
});

export const downloadsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/downloads',
  staticData: { permission: 'tacho.download.read' },
  validateSearch: (s: Record<string, unknown>): DownloadsSearch => ({
    tab: oneOf(s.tab, ['requests', 'schedules', 'attention'] as const),
    status: oneOf(s.status, ['scheduled', 'requested', 'in_progress', 'completed', 'failed', 'timed_out'] as const),
    kind: oneOf(s.kind, ['driver_card', 'vehicle_unit'] as const),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/DownloadsPage')),
});

export const cardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compliance/cards',
  staticData: { permission: 'tacho.card.read' },
  validateSearch: (s: Record<string, unknown>): CardsSearch => ({ selected: str(s.selected) }),
  component: lazyRouteComponent(() => import('./pages/CardsPage')),
});
